import React from 'react'
import { useTranslation } from 'react-i18next'
import { Add as AddIcon, FileCopy as CopyIcon } from '@mui/icons-material'
import {
  Avatar as MuiAvatar,
  Box,
  Button,
  Checkbox,
  Grid,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { useDispatch } from '../store'
import { toggleBlock } from '../store/user/actionCreator'
import { PrivatePage } from '../component/PagePrivate'
import { Subscriber } from '../store/user/reducer'
import { DataTable } from '../component/datatable'
import { useTableStyles } from '../component/datatable/hook/useStyles'
import { TableRef } from '../component/datatable/types'
import { formatDate } from '../function/date'
import { PopupCreateEnterpriseUser } from '../container/user/PopupCreateEnterpriseUser'

// ---------------------------------------------------------------------------
// Enterprise Users list — third "Users" submenu entry alongside Subscribers
// and Users (see container/common/Menu.tsx). Enterprise Users authenticate
// with an access key instead of email/phone+password (espy-backend's
// EnterpriseAccessKeyEntity), so this page mirrors AdminsPage.tsx's simpler
// DataTable pattern rather than SubscribersPage.tsx's richer one — reuses
// the same /users/clients endpoint with an isEnterprise=true filter baked
// into the url (see espy-backend's UsersService#findAllUsers).
// ---------------------------------------------------------------------------

export function EnterprisePage() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const classesTable = useTableStyles()

  const tableRef = React.useRef<TableRef>(null)
  const [copiedId, setCopiedId] = React.useState<number | null>(null)
  const [createOpen, setCreateOpen] = React.useState(false)

  function updateTable() {
    tableRef.current?.refresh()
  }

  const tableHeaderData = [
    { key: 'uid', text: t('enterprise.table.uid') },
    { key: 'user', text: t('enterprise.table.user') },
    { key: 'contact', text: t('enterprise.table.contact') },
    { key: 'company', text: t('enterprise.table.company') },
    { key: 'accessKey', text: t('enterprise.table.accessKey') },
    { key: 'created', text: t('enterprise.table.created') },
    { key: 'blocked', text: t('enterprise.table.blocked') },
  ]

  function handleCopyKey(e: React.MouseEvent<HTMLButtonElement>, row: Subscriber) {
    e.stopPropagation()
    if (!row.enterpriseAccessKey?.key) return
    navigator.clipboard.writeText(row.enterpriseAccessKey.key)
    setCopiedId(row.id)
    setTimeout(() => setCopiedId((state) => (state === row.id ? null : state)), 1500)
  }

  // Mirrors SubscribersPage.tsx's handleRowClick — open the same subscriber
  // detail page (works for Enterprise Users too, it's the same UsersService
  // record under the hood, just fetched by id via GET /users/client/:id).
  function handleRowClick(row: Subscriber) {
    window.open(`/users/subscriber/${row.id}`, '_blank', 'noopener,noreferrer')
  }

  function TableBody(row: Subscriber, idx: number) {
    function handleBlock() {
      dispatch(toggleBlock(row.id, !row.isBlocked, updateTable))
    }

    return (
      <TableRow
        key={row.id}
        className={idx % 2 !== 0 ? classesTable.rowEven : ''}
        hover
        onClick={() => handleRowClick(row)}
        sx={{ cursor: 'pointer' }}
      >
        <TableCell classes={classesTable}>{row.uid}</TableCell>
        <TableCell classes={classesTable}>
          <Grid container alignItems="center">
            <Box>
              <MuiAvatar
                src={row.avatar || ''}
                alt={row.firstname || ''}
                sx={{ width: 30, height: 30 }}
              />
            </Box>
            <Box ml={1}>
              {row.firstname} {row.lastname}
            </Box>
          </Grid>
        </TableCell>
        <TableCell classes={classesTable}>{row.phone || row.email}</TableCell>
        <TableCell classes={classesTable}>{row.companyName || ''}</TableCell>
        <TableCell classes={classesTable}>
          {row.enterpriseAccessKey?.key ? (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                {row.enterpriseAccessKey.key}
              </Typography>
              <Tooltip title={<span>{copiedId === row.id ? t('common.copied', 'Copied') : t('common.copy', 'Copy')}</span>}>
                <IconButton onClick={(e) => handleCopyKey(e, row)} size="small">
                  <CopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : (
            ''
          )}
        </TableCell>
        <TableCell classes={classesTable}>
          {row.enterpriseAccessKey?.createdTime
            ? formatDate(row.enterpriseAccessKey.createdTime, 'dd/MM/yyyy HH:mm:ss')
            : ''}
        </TableCell>
        <TableCell classes={classesTable} onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={row.isBlocked} onChange={handleBlock} readOnly />
        </TableCell>
      </TableRow>
    )
  }

  return (
    <PrivatePage>
      <Stack direction="row" justifyContent="space-between" width="100%">
        <Typography variant="semiBold" fontSize={24}>
          {t('enterprise.title')}
        </Typography>

        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          {t('enterprise.create.button')}
        </Button>
      </Stack>

      <DataTable
        id="enterprise"
        className={classesTable.card}
        ref={tableRef}
        url="/users/clients?isEnterprise=true"
        headerData={tableHeaderData}
        renderBody={TableBody}
        search
        searchPlaceholder="Search by name"
        pagination
        emptyText="No Enterprise Users yet"
      />

      {createOpen && (
        <PopupCreateEnterpriseUser
          onClose={() => setCreateOpen(false)}
          onCreated={updateTable}
        />
      )}
    </PrivatePage>
  )
}
